import { get } from 'svelte/store'
import { getOrderDetails } from '@lib/orders'
import { fn, pageOptsStore } from '@packages/stores'
import { Pages } from '@packages/utils/enums'
import { DEFAULT_TOKEN_ID } from '@packages/utils/constants'
import type { TSubscriptionPurchasePageOpts } from '@packages/utils/types'
import { createSubscription } from '@src/passport/subscription'
import { configStore } from '@src/stores/config'
import { afterPaymentProcess } from '../Payment.service'
import { createOrder, processOrder } from '../order.service'

export type TStripePaymentContext = unknown
export type TStripeAfterConfirmArgs = {
  context?: TStripePaymentContext
  priceCents: number
}

export type TStripePaymentFlow = {
  createPayment: () => Promise<{ clientSecret: string; context?: TStripePaymentContext }>
  afterConfirm: (args: TStripeAfterConfirmArgs) => Promise<void>
  showWalletPay?: boolean
}

const getSubscriptionOpts = () => get(pageOptsStore) as TSubscriptionPurchasePageOpts

const getSubscriptionMembershipResourceId = () => {
  const opts = getSubscriptionOpts()
  return opts.subscription?.membershipResourceId
}

const getSubscriptionTokenId = () => {
  const opts = getSubscriptionOpts()
  return opts.subscription?.tokenId || DEFAULT_TOKEN_ID
}

export const createOrderStripeFlow = (): TStripePaymentFlow => ({
  createPayment: async () => {
    const { preparePaymentData } = await import('@pages/payment/stripe/Stripe.service')
    const config = get(configStore)

    const paymentData = await preparePaymentData()
    const orderId = await createOrder(paymentData)
    const order = await getOrderDetails(config.credenza.generalApiUrl, orderId)
    const clientSecret = order.payment_provider_data?.client_secret
    if (!clientSecret) throw new Error('Payment is unavailable now')

    return { clientSecret, context: orderId }
  },
  afterConfirm: async ({ context, priceCents }) => {
    const orderId = context as string
    const res = await processOrder(orderId)
    await afterPaymentProcess(res, priceCents, get(pageOptsStore))
  },
  showWalletPay: true,
})

export const createSubscriptionStripeFlow = (): TStripePaymentFlow => ({
  createPayment: async () => {
    const opts = getSubscriptionOpts()
    const membershipResourceId = getSubscriptionMembershipResourceId()

    if (!membershipResourceId) throw new Error('Missing subscription membership resource id')

    const subscription = await createSubscription({
      membership_resource_id: membershipResourceId,
      token_id: getSubscriptionTokenId(),
      chain_id: opts.subscription?.chainId,
    })
    const clientSecret = subscription.payment_provider_data?.client_secret
    if (!clientSecret) throw new Error('Subscription payment is unavailable now')

    return { clientSecret, context: subscription }
  },
  afterConfirm: async () => {
    const opts = getSubscriptionOpts()
    const { toastAlert } = get(fn)

    toastAlert('Your subscription was created successfully')
    pageOptsStore.set({
      title: opts.successTitle || 'Success!',
      subtitle: opts.successSubtitle || 'Your subscription was created successfully.',
      receipt: false,
    })
    const { openUI } = get(fn)
    await openUI(Pages.PAYMENT_RESULT)
  },
  showWalletPay: false,
})
